import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Search, RefreshCw, Target, TrendingUp, Building2, Map, ChevronRight,
  Loader2, Star, StarOff, BookmarkPlus, Share2, CheckCircle, XCircle,
  AlertCircle, Minus, Filter, Users, ArrowRight, Sparkles
} from 'lucide-react';

const CATEGORY_COLORS = {
  exact: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  strong: 'bg-blue-100 text-blue-800 border-blue-200',
  close: 'bg-amber-100 text-amber-800 border-amber-200',
  alternative: 'bg-slate-100 text-slate-600 border-slate-200',
  below: 'bg-red-50 text-red-600 border-red-200',
};
const STATUS_ICON = { match: CheckCircle, partial: AlertCircle, mismatch: XCircle, unavailable: Minus };
const STATUS_COLOR = { match: 'text-emerald-600', partial: 'text-amber-500', mismatch: 'text-red-400', unavailable: 'text-slate-300' };

const ScoreBadge = ({ score, category }) => (
  <div className="flex items-center gap-2">
    <div className={`text-lg font-bold ${score >= 85 ? 'text-emerald-700' : score >= 70 ? 'text-blue-700' : score >= 55 ? 'text-amber-700' : 'text-slate-600'}`}>
      {score}%
    </div>
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${CATEGORY_COLORS[category?.key] || CATEGORY_COLORS.below}`}>
      {category?.label || 'Low Match'}
    </span>
  </div>
);

const CriteriaList = ({ criteria }) => (
  <div className="space-y-0.5 mt-2">
    {criteria?.map((c, i) => {
      const Icon = STATUS_ICON[c.status] || Minus;
      return (
        <div key={i} className="flex items-start gap-1.5 text-xs">
          <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${STATUS_COLOR[c.status] || 'text-slate-300'}`} />
          <span className="text-slate-600"><span className="font-medium text-slate-700">{c.name}</span> — {c.explanation}</span>
        </div>
      );
    })}
  </div>
);

const PropertyMatchCard = ({ match, onSave, onShare, onExplain, savedIds, explanation, explaining }) => {
  const [expanded, setExpanded] = useState(false);
  const isSaved = savedIds.has(match.property_id);
  return (
    <Card className="p-3 hover:shadow-md transition-shadow" data-testid={`match-${match.property_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {match.property_type === 'flat' ? <Building2 className="w-4 h-4 text-blue-500 flex-shrink-0" /> : <Map className="w-4 h-4 text-green-600 flex-shrink-0" />}
            <span className="text-sm font-semibold text-slate-900 truncate">{match.project_name}</span>
            <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded capitalize">{match.property_type}</span>
          </div>
          <p className="text-xs text-slate-600">{match.identifier}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-500">
            <span className="font-medium text-slate-800">{match.price_display}</span>
            {match.size && match.size !== '—' && <span>{match.size}</span>}
            {match.bhk && <span>{match.bhk}</span>}
            {match.facing && <span>{match.facing}</span>}
            <span className={`capitalize ${match.status === 'available' ? 'text-emerald-600' : 'text-amber-600'}`}>{match.status}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <ScoreBadge score={match.score} category={match.category} />
          <div className="flex gap-1">
            <button className={`p-1 rounded ${isSaved ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`} onClick={() => onSave(match)} title={isSaved ? 'Saved' : 'Save match'} data-testid={`save-match-${match.property_id}`}>
              {isSaved ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
            </button>
            <button className="p-1 rounded text-slate-300 hover:text-green-600" onClick={() => onShare(match)} title="Prepare share" data-testid={`share-match-${match.property_id}`}>
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <button className="text-[10px] text-blue-600 hover:underline" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Hide details' : 'Show match details'}
        </button>
        <button className="text-[10px] text-purple-600 hover:underline flex items-center gap-0.5" onClick={() => onExplain(match)} disabled={explaining} data-testid={`explain-${match.property_id}`}>
          {explaining ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
          Explain
        </button>
      </div>
      {expanded && <CriteriaList criteria={match.criteria} />}
      {explanation && (
        <p className="text-xs text-slate-600 mt-2 bg-purple-50 rounded px-2.5 py-1.5 border border-purple-100 italic">{explanation}</p>
      )}
    </Card>
  );
};

const PropertyMatching = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadMatches, setLeadMatches] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVertical, setFilterVertical] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [explanations, setExplanations] = useState({});
  const [explainingId, setExplainingId] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/property-matching/dashboard');
      setDashboard(res.data);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const openLeadMatches = async (leadSummary) => {
    setSelectedLead(leadSummary);
    setMatchLoading(true);
    try {
      const [matchRes, savedRes] = await Promise.all([
        api.get(`/leads/${leadSummary.lead_id}/property-matches?limit=20`),
        api.get(`/leads/${leadSummary.lead_id}/saved-matches`),
      ]);
      setLeadMatches(matchRes.data);
      setSavedIds(new Set(savedRes.data.map(s => s.property_id)));
    } catch { toast.error('Failed to load matches'); }
    setMatchLoading(false);
  };

  const refreshMatches = async () => {
    if (!selectedLead) return;
    setMatchLoading(true);
    try {
      const res = await api.get(`/leads/${selectedLead.lead_id}/property-matches?limit=20`);
      setLeadMatches(res.data);
      toast.success('Matches refreshed');
    } catch { toast.error('Failed to refresh'); }
    setMatchLoading(false);
  };

  const handleSave = async (match) => {
    if (savedIds.has(match.property_id)) return;
    try {
      await api.post('/saved-matches', {
        lead_id: selectedLead.lead_id,
        property_id: match.property_id,
        property_type: match.property_type,
        score: match.score,
      });
      setSavedIds(prev => new Set([...prev, match.property_id]));
      toast.success('Match saved');
    } catch { toast.error('Failed to save'); }
  };

  const handleShare = async (match) => {
    if (!selectedLead) return;
    try {
      const res = await api.post('/whatsapp/share-property', {
        lead_id: selectedLead.lead_id,
        property_id: match.property_id,
        property_type: match.property_type,
      });
      const { conversation_id, message_body } = res.data;
      navigate(`/whatsapp?convo=${conversation_id}&prefill=${encodeURIComponent(message_body)}`);
      toast.success('Opening WhatsApp with property details');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to prepare share');
    }
  };

  const handleExplain = async (match) => {
    if (explanations[match.property_id] || !selectedLead) return;
    setExplainingId(match.property_id);
    try {
      const res = await api.post('/property-matching/explain-match', {
        lead_id: selectedLead.lead_id,
        property_data: { project_name: match.project_name, identifier: match.identifier, property_type: match.property_type, price_display: match.price_display, size: match.size, bhk: match.bhk, facing: match.facing },
        criteria: match.criteria,
        score: match.score,
      });
      setExplanations(prev => ({ ...prev, [match.property_id]: res.data.explanation }));
    } catch { toast.error('Could not generate explanation'); }
    setExplainingId(null);
  };

  const filtered = dashboard.filter(d => {
    if (searchTerm && !d.lead_name.toLowerCase().includes(searchTerm.toLowerCase()) && !d.lead_phone?.includes(searchTerm)) return false;
    if (filterVertical !== 'all' && d.lead_vertical !== filterVertical) return false;
    if (filterCategory !== 'all') {
      if (filterCategory === 'has_matches' && d.match_count === 0) return false;
      if (filterCategory === 'no_matches' && d.match_count > 0) return false;
      if (filterCategory === 'strong' && d.top_score < 70) return false;
    }
    return true;
  });

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div></Layout>;

  return (
    <Layout>
    <div className="max-w-full overflow-x-hidden" data-testid="property-matching-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3 px-1">
        <div>
          <h1 className="aavaas-page-title" data-testid="matching-title">Property Matching</h1>
          <p className="text-sm text-slate-500">{dashboard.length} leads · {dashboard.filter(d => d.match_count > 0).length} with matches</p>
        </div>
      </div>

      <div className="flex gap-4 h-[calc(100vh-200px)] min-h-[500px]">
        {/* Lead List */}
        <div className={`${selectedLead ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-[380px] lg:flex-shrink-0 bg-white rounded-xl shadow-sm overflow-hidden`}>
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search leads..." className="pl-10 h-9 bg-slate-50 border-0 text-sm" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="matching-search" />
            </div>
            <div className="flex gap-2">
              <Select value={filterVertical} onValueChange={setFilterVertical}>
                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Verticals</SelectItem>
                  <SelectItem value="plots">Plots</SelectItem>
                  <SelectItem value="flats">Flats</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="has_matches">Has Matches</SelectItem>
                  <SelectItem value="strong">Strong+ (70%+)</SelectItem>
                  <SelectItem value="no_matches">No Matches</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Target className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                <p className="text-sm text-slate-500">No leads found</p>
              </div>
            ) : filtered.map(d => (
              <div key={d.lead_id} className={`px-3 py-3 cursor-pointer border-b border-slate-50 transition-colors ${selectedLead?.lead_id === d.lead_id ? 'bg-blue-50' : 'hover:bg-slate-50'}`} onClick={() => openLeadMatches(d)} data-testid={`lead-match-${d.lead_id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-900 truncate">{d.lead_name}</span>
                  {d.top_score > 0 && <ScoreBadge score={d.top_score} category={d.top_match?.category} />}
                </div>
                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                  <span>{d.lead_budget || '—'}</span>
                  <span className="capitalize">{d.lead_vertical}</span>
                  <span>{d.match_count} match{d.match_count !== 1 ? 'es' : ''}</span>
                </div>
                {d.top_match && (
                  <p className="text-[10px] text-slate-400 mt-1 truncate">Top: {d.top_match.project_name} — {d.top_match.identifier}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Match Results */}
        <div className={`${!selectedLead ? 'hidden lg:flex' : 'flex'} flex-col flex-1 bg-white rounded-xl shadow-sm overflow-hidden`}>
          {!selectedLead ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Target className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                <p className="text-lg font-medium text-slate-400">Select a lead</p>
                <p className="text-sm text-slate-300 mt-1">to see property matches</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <button className="lg:hidden p-1 text-slate-500" onClick={() => setSelectedLead(null)}>
                      <ArrowRight className="w-4 h-4 rotate-180" />
                    </button>
                    <h2 className="text-sm font-semibold text-slate-900 truncate">{selectedLead.lead_name}</h2>
                    <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded capitalize">{selectedLead.lead_vertical}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{selectedLead.lead_budget || 'No budget'} · {leadMatches.length} matches</p>
                </div>
                <Button size="sm" variant="outline" onClick={refreshMatches} disabled={matchLoading} data-testid="refresh-matches-btn">
                  <RefreshCw className={`w-4 h-4 mr-1.5 ${matchLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {matchLoading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : leadMatches.length === 0 ? (
                  <div className="text-center py-12">
                    <Target className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    <p className="text-sm text-slate-500">No matching properties found</p>
                    <p className="text-xs text-slate-400 mt-1">Try updating the lead&apos;s property requirements</p>
                  </div>
                ) : leadMatches.map(m => (
                  <PropertyMatchCard key={m.property_id} match={m} onSave={handleSave} onShare={handleShare} onExplain={handleExplain} savedIds={savedIds} explanation={explanations[m.property_id]} explaining={explainingId === m.property_id} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

    </div>
    </Layout>
  );
};

export default PropertyMatching;
